import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const de = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
const key = (g, t) => `${g.kurs_id}|${t.datum}`;

const Bestaetigt = ({ g, t }) => (
  <div className="flex items-center gap-2 text-sm text-stone-400" data-testid={`dozent-tag-bestaetigt-${g.kurs_id}-${t.datum}`}>
    <input type="checkbox" className="w-4 h-4" disabled checked readOnly />
    <span>{de(t.datum)} · bestätigt von {t.bestaetigt.selbst ? "dir" : t.bestaetigt.name.split(" ")[0]}{t.bestaetigt.am && ` am ${t.bestaetigt.am.split("-").reverse().join(".")}`}</span>
  </div>
);

const Gruppe = ({ g, gewaehlt, toggle, setMehrere }) => {
  const [offen, setOffen] = useState(false);
  const offeneTage = g.tage.filter((t) => !t.bestaetigt);
  const waehlbar = offeneTage.filter((t) => !t.urlaub);
  const anzahl = g.tage.filter((t) => gewaehlt.has(key(g, t))).length;
  const urlaub = offeneTage.length - waehlbar.length;
  const erledigt = g.tage.length - offeneTage.length;
  return (
    <div className="border border-stone-200 rounded-md" data-testid={`dozent-kurs-${g.id}`}>
      <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left" onClick={() => setOffen(!offen)} data-testid={`dozent-kurs-toggle-${g.id}`}>
        {offen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <b>{g.kurs_id}</b> · {de(g.von)} – {de(g.bis)} · {offeneTage.length} {offeneTage.length === 1 ? "Tag" : "Tage"} offen
        {urlaub > 0 && <span className="text-xs text-amber-700">({urlaub} davon in deinem Urlaub)</span>}
        {erledigt > 0 && <span className="text-xs text-stone-500" data-testid={`dozent-kurs-erledigt-${g.id}`}>· {erledigt} schon bestätigt</span>}
        {anzahl > 0 && <span className="ml-auto text-xs font-semibold text-emerald-700">{anzahl} ausgewählt</span>}
      </button>
      {offen && (
        <div className="px-3 pb-3">
          <button type="button" className="text-xs underline text-stone-500 mb-2" onClick={() => setMehrere(waehlbar.map((t) => key(g, t)), anzahl < waehlbar.length)} data-testid={`dozent-kurs-alle-${g.id}`}>
            {anzahl < waehlbar.length ? "alle meine Tage dieses Kurses auswählen" : "Auswahl aufheben"}
          </button>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
            {g.tage.map((t) => t.bestaetigt ? <Bestaetigt key={t.datum} g={g} t={t} /> : (
              <label key={t.datum} className={`flex items-center gap-2 text-sm ${t.urlaub ? "text-stone-400" : "cursor-pointer"}`} data-testid={`dozent-tag-${g.kurs_id}-${t.datum}`}>
                <input type="checkbox" className="w-4 h-4" disabled={t.urlaub} checked={gewaehlt.has(key(g, t))} onChange={() => toggle(key(g, t))} />
                {de(t.datum)}{t.urlaub && " · Urlaub"}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const DozentNachtragBlock = () => {
  const [d, setD] = useState(null);
  const [gewaehlt, setGewaehlt] = useState(new Set());
  const laden = () => axios.get(`${API}/dozent/nachtraege`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { laden(); }, []);
  if (!d?.darf_bestaetigen || !d.gruppen?.length) return null;
  const toggle = (k) => setGewaehlt((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const setMehrere = (keys, an) => setGewaehlt((s) => { const n = new Set(s); keys.forEach((k) => (an ? n.add(k) : n.delete(k))); return n; });
  const bestaetigen = async () => {
    if (!window.confirm(`Ich bestätige, dass ich an den ${gewaehlt.size} ausgewählten Kurstagen selbst unterrichtet habe. Meine Unterschrift erscheint danach auf den Anwesenheitslisten dieser Tage.`)) return;
    const tage = [...gewaehlt].map((k) => { const i = k.lastIndexOf("|"); return { kurs_id: k.slice(0, i), datum: k.slice(i + 1) }; });
    try { const r = await axios.post(`${API}/dozent/tage-bestaetigen`, { bestaetigt: true, tage }); toast.success(`${r.data.bestaetigt} Kurstage bestätigt`); setGewaehlt(new Set()); laden(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };
  return (
    <div className="border-2 border-[#B4502F] rounded-lg p-4 mb-4" data-testid="dozent-nachtrag-block">
      <p className="text-sm">Für diese Kurstage ist <b>kein Dozent</b> eingetragen. Klapp den Kurs auf und hake <b>nur die Tage an, an denen du selbst unterrichtet hast</b>. Tage einer Vertretung bitte offen lassen, die bestätigt die Kollegin selbst.</p>
      <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
        {d.gruppen.map((g) => <Gruppe key={g.id} g={g} gewaehlt={gewaehlt} toggle={toggle} setMehrere={setMehrere} />)}
      </div>
      <Button size="sm" className="mt-3" disabled={!gewaehlt.size} onClick={bestaetigen} data-testid="dozent-tage-bestaetigen">
        {gewaehlt.size ? `${gewaehlt.size} Tage als meine bestätigen` : "Tage auswählen"}
      </Button>
    </div>
  );
};
